import { Plus } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { UpsertComponent } from 'bom/components';
import { BomPartViewByType, ComponentStatus } from 'bom/enums';
import { IElement } from 'bom/types';
import { Button } from 'core/components';
import { MenuButton } from 'core/elements';
import { ApiStatus, NotificationPosition } from 'core/enums';
import { useAgGridClientTableContext, useNotificationContext } from 'core/states';
import { UpsertElement, UpsertLine, useViewByContext } from 'jobs/components/JobDetails';
import { Color, Size, Variant } from 'theme/enums';

interface IProps {
  jobId: number;
  element?: IElement;
}

export const AddLineButton = ({ jobId, element }: IProps) => {
  const [isAddLineOpen, setIsAddLineOpen] = useState(false);
  const [isAddElementOpen, setIsAddElementOpen] = useState(false);
  const [isAddComponentOpen, setIsAddComponentOpen] = useState(false);

  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const { gridApi, rows, updateRows } = useAgGridClientTableContext();
  const { viewBy, setViewBy } = useViewByContext();
  const { clearNotification, showNotification } = useNotificationContext();

  const isAddElementDisabled = useMemo(() => !!element?.id, [element?.id]);

  const onAddLineSubmitSuccess = async () => {
    // When add a new line globally (not within an element), we don't know which row to scroll to
    // (the element could be collapsed)
    if (viewBy !== BomPartViewByType.PART && !element?.id) {
      return;
    }

    setTimeout(() => {
      scrollToLastActiveRow();
    }, 100);
  };

  const onAddElementSubmitSuccess = async (element: IElement) => {
    if (viewBy !== BomPartViewByType.ELEMENT) {
      queryClient.invalidateQueries('jobParts');

      showNotification({
        title: t('titleAddElementViewByPartInfo'),
        message: '',
        buttons: [
          <Button
            key="changeToViewByElement"
            text={t('buttonLabelChangeToViewByElement')}
            variant={Variant.SOFT}
            onClick={() => {
              clearNotification();
              setViewBy(BomPartViewByType.ELEMENT);
            }}
          />,
        ],
        type: ApiStatus.INFO,
        position: NotificationPosition.TOP,
        disableAutoClose: true,
      });

      return;
    }

    updateRows({ add: [element] });
  };

  const onAddComponentSubmitSuccess = async () => {
    queryClient.invalidateQueries('jobParts');

    // When add a new component globally (not within an element), we don't know which row to scroll
    // to (the element could be collapsed)
    if (viewBy !== BomPartViewByType.PART && !element?.id) {
      return;
    }

    setTimeout(() => {
      scrollToLastActiveRow();
    }, 100);
  };

  const scrollToLastActiveRow = () => {
    const firstDeletedRowIndex = rows.findIndex((row) => row.status === ComponentStatus.DELETED);

    const lastRowIndex =
      firstDeletedRowIndex === -1 ? gridApi.getDisplayedRowCount() - 1 : firstDeletedRowIndex;

    gridApi.ensureIndexVisible(lastRowIndex, 'bottom');
  };

  return (
    <>
      <MenuButton
        label={t('buttonLabelAdd')}
        color={Color.SUCCESS}
        variant={Variant.SOLID}
        startDecorator={<Plus />}
        menuItems={[
          {
            label: t('buttonLabelAddLine'),
            onClick: () => setIsAddLineOpen(true),
          },
          {
            label: t('buttonLabelAddElement'),
            onClick: () => setIsAddElementOpen(true),
            disabled: isAddElementDisabled,
          },
          {
            label: t('buttonLabelAddComponent'),
            onClick: () => setIsAddComponentOpen(true),
          },
        ]}
        menuSize={Size.MD}
      />

      <UpsertLine
        jobId={jobId}
        element={element}
        isOpen={isAddLineOpen}
        onClose={() => setIsAddLineOpen(false)}
        onSubmitSuccess={onAddLineSubmitSuccess}
      />

      {!isAddElementDisabled && (
        <UpsertElement
          jobId={jobId}
          isOpen={isAddElementOpen}
          onClose={() => setIsAddElementOpen(false)}
          onSubmitSuccess={onAddElementSubmitSuccess}
        />
      )}

      <UpsertComponent
        jobId={jobId}
        element={element}
        isOpen={isAddComponentOpen}
        onClose={() => setIsAddComponentOpen(false)}
        onSubmitSuccess={onAddComponentSubmitSuccess}
      />
    </>
  );
};
